import React from 'react'
import CSSTransitionGroup from 'react-addons-css-transition-group'
import { RangeBlocks } from '../RangeBlocks'
import { useProducts, useFilters } from '../../context'
import { LoadMore, ProductSort } from '.'
import { ProductWithActiveColour } from '../../types'
import { getProductColour, filtersActive, dimensionsActive } from '../../utils'
import { sortProducts } from '../../utils/sortProducts'
import { ProductsLoadingIndicator } from './ProductsLoadingIndicator'
import { ErrorMessage } from '../ErrorMessage'
import { ProductList } from './ProductList'

const PER_PAGE = 12

export const ProductGrid: React.FC = () => {
  const filtersContext = useFilters()
  const { products, productsError, productsLoading } = useProducts()

  if (!filtersContext) return null

  const {
    activeFilters,
    activeDimensions,
    filters,
    page,
    onSale,
    sort,
    clearance,
  } = filtersContext

  if (productsError) return <ErrorMessage message={productsError.message} />

  // NB: Filter products using active filters
  const activeProducts = products.reduce((array, product) => {
    const colours = activeFilters.colour
    const ranges = activeFilters.range
    const seatTypes = activeFilters.seatType
    const height = activeDimensions.height
    const width = activeDimensions.width
    const depth = activeDimensions.depth

    const productColours = Object.keys(product.colours).map(
      col => product.colours[col].title
    )
    let include = false

    if (
      (!ranges.size || ranges.has(product.range)) &&
      (!seatTypes.size || seatTypes.has(product.seatType)) &&
      (!colours.size || productColours.some(r => colours.has(r)))
    ) {
      include = true
    }

    // check height is between set range
    if (
      (height && !product.height) ||
      (height && product.height && (parseFloat(product.height) > parseFloat(height.max) || parseFloat(product.height) < parseFloat(height.min)))
    ) {
      include = false
    }

    // check width is between set range
    if (
      width && product.width &&
      (parseFloat(product.width) > parseFloat(width.max) || parseFloat(product.width) < parseFloat(width.min))
    ) {
      include = false
    }

    // check depth is between set range
    if (
      depth && product.depth &&
      (parseFloat(product.depth) > parseFloat(depth.max) || parseFloat(product.depth) < parseFloat(depth.min))
    ) {
      include = false
    }

    if (clearance && !product.clearance) include = false
    if (!clearance && product.clearance) include = false

    if (include) {
      array.push({
        ...product,
        activeColour: getProductColour(product, activeFilters, onSale),
      })
    }

    return array
  }, Array<ProductWithActiveColour>())

  const sortedProducts = sortProducts(activeProducts, filters, sort)
  const pages = Math.ceil(sortedProducts.length / PER_PAGE)
  const paginatedProducts = [...sortedProducts].slice(0, page * PER_PAGE)
  const areFiltersActive = filtersActive(filtersContext) || dimensionsActive(filtersContext)

  return (
    <div className="productlist">
      <ProductsLoadingIndicator />

      <ProductSort />

      <CSSTransitionGroup
        component="div"
        className="productlist-products"
        transitionName="product"
        transitionEnterTimeout={200}
        transitionLeaveTimeout={200}
      >
        {areFiltersActive ? (
          <ProductList products={paginatedProducts} />
        ) : (
          <RangeBlocks />
        )}
      </CSSTransitionGroup>

      <CSSTransitionGroup
        component="div"
        transitionName="product"
        transitionEnterTimeout={200}
        transitionLeaveTimeout={200}
      >
        {!productsLoading && paginatedProducts.length === 0 && (
          <p className="productlist-noresult">No results found</p>
        )}
      </CSSTransitionGroup>
      {areFiltersActive && <LoadMore pages={pages} />}
    </div>
  )
}
